"use client";

import React from "react";
import { Banner } from "@/app/components/MoveBrasil/Banner";
import { BannerImage } from "@/app/components/MoveBrasil/BannerImage";
import { ConditionsSection } from "@/app/components/MoveBrasil/ConditionsSection";
import { ContactSection } from "@/app/components/MoveBrasil/ContactSection";
import { FaqSection } from "@/app/components/MoveBrasil/FaqSection";
import { HowItWorksSection } from "@/app/components/MoveBrasil/HowItWorksSection";
import { ModelSection } from "@/app/components/MoveBrasil/ModelSection";
import {
	MOVE_BRASIL_FORM_ID,
	MOVE_BRASIL_IMAGES,
} from "@/app/config/moveBrasil";
import { useIframeHeightPostMessage } from "@/app/hooks/useIframeHeightPostMessage";
import styles from "@/styles/move-brasil.module.scss";

function scrollToForm(e: React.MouseEvent<HTMLAnchorElement>) {
	e.preventDefault();
	document
		.getElementById(MOVE_BRASIL_FORM_ID)
		?.scrollIntoView({ behavior: "smooth", block: "start" });
}

export const MoveBrasilPage: React.FC = () => {
	const pageRef = useIframeHeightPostMessage("move-brasil-jeep");

	return (
		<div ref={pageRef} className={styles.page}>
			<Banner />

			<section className={styles.intro}>
				<div className={styles.container}>
					<div className={styles.introGrid}>
						<figure className={styles.introImageWrap}>
							<BannerImage
								src={MOVE_BRASIL_IMAGES.carIntro}
								alt="Jeep Renegade"
								className={styles.introImage}
								fallbackClassName={styles.introImageFallback}
							/>
						</figure>

						<div className={styles.introContent}>
							<h1 className={styles.introTitle}>
								É motorista de{" "}
								<span className={styles.highlight}>
									aplicativo ou taxista?
								</span>
							</h1>

							<p className={styles.introText}>
								O <strong>Move Brasil</strong> Táxi e Aplicativos é uma
								linha de crédito do Governo Federal para motoristas de
								aplicativo e taxistas que desejam modernizar sua frota.
							</p>

							<p className={styles.introText}>
								O{" "}
								<strong>
									Jeep Renegade Longitude T270 MHEV 4x2
								</strong>{" "}
								faz parte do programa. Tenha mais eficiência, conforto e
								segurança na sua rotina, com condições diferenciadas de
								financiamento.
							</p>

							<a
								className={styles.ctaButton}
								href={`#${MOVE_BRASIL_FORM_ID}`}
								onClick={scrollToForm}
							>
								GARANTA O SEU
								<span className={styles.ctaArrow} aria-hidden="true">
									<svg
										width="10"
										height="14"
										viewBox="0 0 10 14"
										fill="none"
										xmlns="http://www.w3.org/2000/svg"
									>
										<path
											d="M1 1L8 7L1 13"
											stroke="currentColor"
											strokeWidth="2"
											strokeLinecap="round"
											strokeLinejoin="round"
										/>
									</svg>
								</span>
							</a>
						</div>
					</div>
				</div>
			</section>

			<section className={styles.eligibility}>
				<div className={styles.container}>
					<header className={styles.eligibilityHeader}>
						<h2 className={styles.eligibilityTitle}>
							Quem pode participar do{" "}
							<span className={styles.highlight}>Move Brasil?</span>
						</h2>
						<span
							className={styles.eligibilityTitleAccent}
							aria-hidden="true"
						/>
						<p className={styles.eligibilitySubtitle}>
							O Programa Move Brasil possui critérios* específicos para
							participação.
						</p>
					</header>

					<article className={`${styles.card} ${styles.cardTextEnd}`}>
						<div className={styles.cardContent}>
							<div className={styles.cardTextBlock}>
								<span
									className={styles.cardAccentVertical}
									aria-hidden="true"
								/>
								<div className={styles.cardTextInner}>
									<h3 className={styles.cardTitle}>
										Motoristas de aplicativo
									</h3>
									<div
										className={styles.cardAccent}
										aria-hidden="true"
									/>
									<ul className={styles.cardList}>
										<li>
											Possuir cadastro ativo há pelo menos 12 meses na
											mesma plataforma.
										</li>
										<li>
											Ter realizado, no mínimo, 100 corridas nesse
											período.
										</li>
										<li>
											Atender aos critérios de validação definidos pelo
											programa.
										</li>
									</ul>
								</div>
							</div>
						</div>
						<figure className={styles.cardImageWrap}>
							<BannerImage
								src={MOVE_BRASIL_IMAGES.carApp}
								alt="Jeep Renegade — motoristas de aplicativo"
								className={styles.cardImage}
								fallbackClassName={styles.cardImageFallback}
							/>
						</figure>
					</article>

					<article className={`${styles.card} ${styles.cardReverse}`}>
						<figure className={styles.cardImageWrap}>
							<BannerImage
								src={MOVE_BRASIL_IMAGES.carTaxi}
								alt="Jeep Renegade — taxistas"
								className={styles.cardImage}
								fallbackClassName={styles.cardImageFallback}
							/>
						</figure>
						<div className={styles.cardContent}>
							<div className={styles.cardTextBlock}>
								<span
									className={styles.cardAccentVertical}
									aria-hidden="true"
								/>
								<div className={styles.cardTextInner}>
									<h3 className={styles.cardTitle}>Taxistas</h3>
									<div
										className={styles.cardAccent}
										aria-hidden="true"
									/>
									<ul className={styles.cardList}>
										<li>
											Titulares de autorização, permissão ou concessão
											do Poder Público.
										</li>
										<li>
											Ter licenças e registros ativos/regularizados.
										</li>
										<li>
											Cooperativas de Taxi com convênio ativo com
											bancos parceiros Move Brasil.
										</li>
										<li>
											Atender às exigências de documentação e
											regularidade fiscal.
										</li>
									</ul>
								</div>
							</div>
						</div>
					</article>

					<p className={styles.eligibilityDisclaimer}>
						*O programa é exclusivo para pessoas físicas (CPF) e permite a
						compra de um veículo por beneficiário.
					</p>
				</div>
			</section>

			<ConditionsSection />

			<ModelSection />

			<HowItWorksSection />

			<FaqSection />

			<ContactSection />
		</div>
	);
};
